<app-modal #modal [size]="ModalSize.Lg">
  <div class="modal-header">
    <h2 class="modal-title">Merge Patients</h2>
  </div>

  <div class="modal-body" [formGroup]="form">

    <!-- Step 1: Search for source patient -->
    <div *ngIf="currentStep === 1">
      <div class="form-group">
        <label>Search for source patient (will be archived)</label>
        <app-patient-autocomplete
          [parentForm]="form"
          controlName="sourcePatientId"
          [globalOnly]="true"
          placeholder="Search by name, email or patient code..."
          (selectionChanged)="onSourcePatientSelected($event)">
        </app-patient-autocomplete>
      </div>
    </div>

    <!-- Step 2: Search for target patient -->
    <div *ngIf="currentStep === 2">
      <div class="form-group">
        <label>Search for target patient (data will be merged here)</label>
        <app-patient-autocomplete
          [parentForm]="form"
          controlName="targetPatientId"
          [globalOnly]="true"
          placeholder="Search by name, email or patient code..."
          (selectionChanged)="onPatientSelected($event)">
        </app-patient-autocomplete>
      </div>
    </div>

    <!-- Step 3: Side-by-side comparison -->
    <div *ngIf="currentStep === 3 && targetPatientInfo">
      <p class="merge-info-text">Review the patients below before confirming the merge. All data from the <strong>source patient</strong> will be moved to the <strong>target patient</strong> and the source patient will be archived.</p>
      <div class="patient-merge-details">
        <div class="row">
          <div class="col-12 col-md-6">
            <div class="patient-match-card">
              <div class="patient-match-card-content">
                <p class="patient-match-label"><strong>Source patient (will be archived)</strong></p>
                <h2>{{ sourcePatientInfo.firstname }} {{ sourcePatientInfo.lastname }}</h2>

                <label>Email</label>
                <p>{{ sourcePatientInfo.email }}</p>

                <label>Patient Code</label>
                <p>{{ sourcePatientInfo.patientCode || '-' }}</p>

                <label>{{ sourcePatientInfo.trials?.length === 1 ? 'Trial' : 'Trials' }}</label>
                <p>
                  <span *ngFor="let t of sourcePatientInfo.trials; let last = last">
                    {{ t.trialLabel || t.trialCode }}<span *ngIf="!last">, </span>
                  </span>
                  <span *ngIf="!sourcePatientInfo.trials?.length">-</span>
                </p>
              </div>
            </div>
          </div>

          <div class="col-12 col-md-6">
            <div class="patient-match-card">
              <div class="patient-match-card-content">
                <p class="patient-match-label"><strong>Target patient (data will be merged here)</strong></p>
                <h2>{{ targetPatientInfo.firstname }} {{ targetPatientInfo.lastname }}</h2>

                <label>Email</label>
                <p>{{ targetPatientInfo.email }}</p>

                <label>Patient Code</label>
                <p>{{ targetPatientInfo.patientCode || '-' }}</p>

                <label>{{ targetPatientInfo.trials?.length === 1 ? 'Trial' : 'Trials' }}</label>
                <p>
                  <span *ngFor="let t of targetPatientInfo.trials; let last = last">
                    {{ t.trialLabel || t.trialCode }}<span *ngIf="!last">, </span>
                  </span>
                  <span *ngIf="!targetPatientInfo.trials?.length">-</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Data summary: all records being moved from source patient -->
      <div class="patient-merge-data-summary">
        <h4 class="merge-section-title">Data being moved from source patient</h4>
        <div class="merge-stats-grid">
          <div *ngIf="sourcePatientInfo.visitCount > 0" class="merge-stat-card">
            <span class="merge-stat-count">{{ sourcePatientInfo.visitCount }}</span>
            <span class="merge-stat-label">{{ sourcePatientInfo.visitCount === 1 ? 'Visit' : 'Visits' }}</span>
          </div>
          <div *ngIf="sourcePatientInfo.tripCount > 0" class="merge-stat-card">
            <span class="merge-stat-count">{{ sourcePatientInfo.tripCount }}</span>
            <span class="merge-stat-label">{{ sourcePatientInfo.tripCount === 1 ? 'Trip' : 'Trips' }}</span>
          </div>
          <div *ngIf="sourcePatientInfo.travelRequestCount > 0" class="merge-stat-card">
            <span class="merge-stat-count">{{ sourcePatientInfo.travelRequestCount }}</span>
            <span class="merge-stat-label">{{ sourcePatientInfo.travelRequestCount === 1 ? 'Travel Request' : 'Travel Requests' }}</span>
          </div>
          <div *ngIf="sourcePatientInfo.patientNoteCount > 0" class="merge-stat-card">
            <span class="merge-stat-count">{{ sourcePatientInfo.patientNoteCount }}</span>
            <span class="merge-stat-label">{{ sourcePatientInfo.patientNoteCount === 1 ? 'Note' : 'Notes' }}</span>
          </div>
          <div *ngIf="sourcePatientInfo.caregiverCount > 0" class="merge-stat-card">
            <span class="merge-stat-count">{{ sourcePatientInfo.caregiverCount }}</span>
            <span class="merge-stat-label">{{ sourcePatientInfo.caregiverCount === 1 ? 'Caregiver' : 'Caregivers' }}</span>
          </div>
          <div *ngIf="sourcePatientInfo.notificationCount > 0" class="merge-stat-card">
            <span class="merge-stat-count">{{ sourcePatientInfo.notificationCount }}</span>
            <span class="merge-stat-label">{{ sourcePatientInfo.notificationCount === 1 ? 'Notification' : 'Notifications' }}</span>
          </div>
          <div *ngIf="sourcePatientInfo.bookingCount > 0" class="merge-stat-card">
            <span class="merge-stat-count">{{ sourcePatientInfo.bookingCount }}</span>
            <span class="merge-stat-label">{{ sourcePatientInfo.bookingCount === 1 ? 'Booking' : 'Bookings' }}</span>
          </div>
          <div *ngIf="sourcePatientInfo.visitReminderCount > 0" class="merge-stat-card">
            <span class="merge-stat-count">{{ sourcePatientInfo.visitReminderCount }}</span>
            <span class="merge-stat-label">{{ sourcePatientInfo.visitReminderCount === 1 ? 'Visit Reminder' : 'Visit Reminders' }}</span>
          </div>
          <div *ngIf="sourcePatientInfo.fileAttachmentCount > 0" class="merge-stat-card">
            <span class="merge-stat-count">{{ sourcePatientInfo.fileAttachmentCount }}</span>
            <span class="merge-stat-label">{{ sourcePatientInfo.fileAttachmentCount === 1 ? 'Document' : 'Documents' }}</span>
          </div>
          <div *ngIf="sourcePatientInfo.hasNotes" class="merge-stat-card merge-stat-card--append">
            <span class="merge-stat-append-icon">Appended</span>
            <span class="merge-stat-label">Admin Notes</span>
          </div>
          <div *ngIf="sourcePatientInfo.hasPolicyReminders" class="merge-stat-card merge-stat-card--append">
            <span class="merge-stat-append-icon">Appended</span>
            <span class="merge-stat-label">Policy Reminders</span>
          </div>
          <div *ngIf="sourcePatientInfo.hasCaregiverDetails" class="merge-stat-card merge-stat-card--append">
            <span class="merge-stat-append-icon">Appended</span>
            <span class="merge-stat-label">Caregiver Details</span>
          </div>
          <div *ngIf="!sourcePatientInfo.visitCount && !sourcePatientInfo.tripCount && !sourcePatientInfo.travelRequestCount && !sourcePatientInfo.patientNoteCount && !sourcePatientInfo.caregiverCount && !sourcePatientInfo.notificationCount && !sourcePatientInfo.bookingCount && !sourcePatientInfo.visitReminderCount && !sourcePatientInfo.fileAttachmentCount && !sourcePatientInfo.hasNotes && !sourcePatientInfo.hasPolicyReminders && !sourcePatientInfo.hasCaregiverDetails">
            <span class="text-muted">No additional data to move.</span>
          </div>
        </div>
      </div>

    </div>

  </div>

  <div class="modal-footer">
    <div class="row" *ngIf="currentStep === 1">
      <div class="col-6">
        <a (click)="hideModal()" class="btn btn-secondary" data-dismiss="modal">Cancel</a>
      </div>
      <div class="col-6">
        <button class="btn btn-primary" [disabled]="!canProceedStep1" (click)="onNext()">
          <span *ngIf="loadingStep">
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <span class="sr-only">Loading...</span>
          </span>
          <span *ngIf="!loadingStep">Next</span>
        </button>
      </div>
    </div>

    <div class="row" *ngIf="currentStep === 2">
      <div class="col-6">
        <a (click)="onBack()" class="btn btn-secondary" [class.disabled]="loadingStep">Back</a>
      </div>
      <div class="col-6">
        <button class="btn btn-primary" [disabled]="!canProceedStep2" (click)="onNext()">
          <span *ngIf="loadingStep">
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <span class="sr-only">Loading...</span>
          </span>
          <span *ngIf="!loadingStep">Next</span>
        </button>
      </div>
    </div>

    <div class="row" *ngIf="currentStep === 3">
      <div class="col-6">
        <a (click)="onBack()" class="btn btn-secondary" [class.disabled]="form.get('processing').value">Back</a>
      </div>

      <div class="col-6">
        <button [disabled]="form.get('processing').value" (click)="onConfirmMerge()" class="btn btn-primary">
          <span *ngIf="form.get('processing').value">
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <span class="sr-only">Loading...</span>
          </span>
          <span *ngIf="!form.get('processing').value">Confirm Merge</span>
        </button>
      </div>
    </div>
  </div>
</app-modal>
